// Iter 269 — Consolidated top-nav with nested submenus.
// The center cluster is compacted to 4 primary destinations:
//   Schedule (pill)   |   Live ▾   |   Predict ▾   |   Compete ▾   |   Performance ▾   |   GOLD
// where "Predict" nests Markets as a submenu (Halftime, Double Chance,
// Asian Handicap, Goal Timing) so we no longer need a 6-pill row that
// overflows on 1440-1920px screens.
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import {
  Zap, User, LogIn, Clock, Archive, BarChart3, Sparkles, Trophy, Star,
  Activity, ChevronDown, Wallet, LogOut, Settings, Search as SearchIcon,
  CalendarDays, ShieldCheck, Target, LayoutGrid, Radio, Brain, Layers, Users,
} from "lucide-react";
import SearchBar from "./SearchBar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

// ─── Sole always-visible pill (single primary destination) ─────────
const PRIMARY_PILL = {
  to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "schedule-nav-btn",
};

// "Live ▾" — real-time signal surfaces.
const LIVE_LINKS = [
  { to: "/live",    labelKey: "nav.live", label: "Live matches", icon: Radio,    testid: "live-nav-btn" },
  { to: "/live-ai", label: "Live AI",                             icon: Brain,    testid: "live-ai-nav-btn" },
];

// "Predict ▾" — every prediction / betting surface, with Markets nested.
const PREDICT_TOP = [
  { to: "/coach",             label: "Bet Coach",   icon: Wallet,   testid: "coach-nav-btn" },
  { to: "/account/betslips",  label: "Daily Slips", icon: Sparkles, testid: "nav-betslips-btn" },
];
const PREDICT_MARKETS = [
  { to: "/halftime",       labelKey: "nav.halftime", label: "Halftime",       icon: Clock,       testid: "halftime-nav-btn" },
  { to: "/double-chance",  label: "Double Chance",                             icon: ShieldCheck, testid: "double-chance-nav-btn" },
  { to: "/asian-handicap", label: "Asian Handicap",                            icon: Target,      testid: "asian-handicap-nav-btn" },
  { to: "/goal-timing",    label: "Goal Timing",                               icon: LayoutGrid,  testid: "goal-timing-nav-btn" },
];

// "Compete ▾" — tournaments + follow-team surfaces.
const COMPETE_LINKS = [
  { to: "/world-cup",   label: "World Cup",           icon: Trophy,     testid: "world-cup-nav-btn" },
  { to: "/tournaments", labelKey: "nav.tournaments",  label: "Tournaments", icon: Layers, testid: "tournaments-nav-btn" },
  { to: "/my-teams",    labelKey: "nav.myTeams",      label: "My Teams",    icon: Users,  testid: "my-teams-nav-btn" },
  { to: "/search",      labelKey: "nav.search",       label: "Search",      icon: SearchIcon, testid: "explore-search-btn" },
];

// "Performance ▾" — engine track record.
const PERFORMANCE_LINKS = [
  { to: "/history",  labelKey: "nav.history",  label: "History",           icon: Archive,   testid: "history-nav-btn" },
  { to: "/accuracy", labelKey: "nav.accuracy", label: "Accuracy",          icon: BarChart3, testid: "accuracy-nav-btn" },
  { to: "/analytics/confidence-trend",         label: "Confidence Trend",  icon: Activity,  testid: "confidence-trend-nav-btn" },
];

const NavPill = ({ to, icon: Icon, label, testid, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`relative inline-flex items-center gap-1.5 px-2 py-5 text-[13px] transition-colors ${
      active
        ? "text-foreground"
        : "text-muted-foreground hover:text-foreground"
    }`}
    style={{ fontWeight: active ? 500 : 400, letterSpacing: "-0.005em" }}
  >
    <Icon className="w-[14px] h-[14px]" strokeWidth={1.75} />
    {label}
    {active && (
      <span
        className="absolute left-0 right-0 bottom-0 h-px"
        style={{ background: "var(--v3-text)" }}
      />
    )}
  </button>
);

const resolveLabel = (l, t) => (l.labelKey ? t(l.labelKey) : l.label);

const DropdownRow = ({ item, navigate, pathname, t }) => {
  const Icon = item.icon;
  const isActive = pathname === item.to;
  return (
    <DropdownMenuItem
      key={item.to}
      onSelect={() => navigate(item.to)}
      data-testid={item.testid}
      className={`cursor-pointer flex items-center gap-2.5 py-2 text-[13px] ${
        isActive ? "text-[color:var(--v3-text)] bg-[color:var(--v3-raised)]" : "text-[color:var(--v3-text-2)]"
      }`}
    >
      <Icon className="w-[14px] h-[14px] text-muted-foreground" strokeWidth={1.75} />
      {resolveLabel(item, t)}
    </DropdownMenuItem>
  );
};

const GroupDropdown = ({ label, items, active, testidPrefix, t, navigate, pathname }) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      data-testid={`${testidPrefix}-trigger`}
      className={`relative inline-flex items-center gap-1 px-2 py-5 text-[13px] transition-colors ${
        active
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
      style={{ fontWeight: active ? 500 : 400, letterSpacing: "-0.005em" }}
    >
      {label}
      <ChevronDown className="w-[12px] h-[12px] -mr-0.5" strokeWidth={1.75} />
      {active && (
        <span
          className="absolute left-0 right-0 bottom-0 h-px"
          style={{ background: "var(--v3-text)" }}
        />
      )}
    </DropdownMenuTrigger>
    <DropdownMenuContent align="center" className="min-w-[220px]">
      {items.map((l) => (
        <DropdownRow key={l.to} item={l} navigate={navigate} pathname={pathname} t={t} />
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Predict dropdown includes a nested "Markets ▸" submenu.
const PredictDropdown = ({ active, marketsActive, t, navigate, pathname }) => (
  <DropdownMenu>
    <DropdownMenuTrigger
      data-testid="nav-predict-trigger"
      className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
        active
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}
    >
      Predict
      <ChevronDown className="w-3.5 h-3.5 -mr-0.5" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="center" className="min-w-[220px]">
      {PREDICT_TOP.map((l) => (
        <DropdownRow key={l.to} item={l} navigate={navigate} pathname={pathname} t={t} />
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          data-testid="nav-predict-markets-subtrigger"
          className={`cursor-pointer flex items-center gap-2 py-2 ${
            marketsActive ? "bg-foreground/[0.06] font-semibold" : ""
          }`}
        >
          <Layers className="w-4 h-4 text-muted-foreground" />
          Markets
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="min-w-[200px]">
            {PREDICT_MARKETS.map((l) => (
              <DropdownRow key={l.to} item={l} navigate={navigate} pathname={pathname} t={t} />
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    </DropdownMenuContent>
  </DropdownMenu>
);

const AccountMenu = ({ user, navigate, logout, t }) => {
  const handleLogout = () => { logout(); toast.success("Logged out"); navigate("/"); };
  const initial = (user.email || "?")[0].toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="account-menu-trigger"
        className="inline-flex items-center gap-2 h-9 rounded-full pl-1 pr-3 border border-border bg-card hover:bg-muted transition-colors"
      >
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-background font-bold text-xs flex items-center justify-center">
          {initial}
        </span>
        <span className="hidden sm:inline text-xs font-semibold tracking-tight">
          {(user.email || "").split("@")[0]}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">
          {(user.email || "").split("@")[0]}
          <div className="text-[10px] normal-case tracking-normal text-muted-foreground/70 mt-0.5 truncate">
            {user.email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => navigate("/account")}
          data-testid="account-menu-account"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <Settings className="w-4 h-4 text-muted-foreground" /> Account settings
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/account/earnings")}
          data-testid="account-menu-earnings"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <Wallet className="w-4 h-4 text-emerald-500" /> Earnings tracker
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/account/betslips")}
          data-testid="account-menu-betslips"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <Sparkles className="w-4 h-4 text-amber-500" /> My betslip history
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/auto-bet")}
          data-testid="account-menu-auto-bet"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <Sparkles className="w-4 h-4 text-emerald-400" /> Autonomous bets
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/autopilot")}
          data-testid="account-menu-autopilot"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <Sparkles className="w-4 h-4 text-amber-400" /> Daily Autopilot
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/betway-leagues")}
          data-testid="account-menu-betway-leagues"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Betway Leagues
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => navigate("/team-aliases")}
          data-testid="account-menu-team-aliases"
          className="cursor-pointer flex items-center gap-2 py-2"
        >
          <ShieldCheck className="w-4 h-4 text-sky-400" /> Team Aliases
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleLogout}
          data-testid="account-menu-logout"
          className="cursor-pointer flex items-center gap-2 py-2 text-rose-500 focus:bg-rose-500/10 focus:text-rose-500"
        >
          <LogOut className="w-4 h-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TopNav = ({ onPlaceParlayClick }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const headerQuery = pathname === "/search" ? searchParams.get("q") || "" : "";

  const liveActive = LIVE_LINKS.some((l) => pathname.startsWith(l.to));
  const marketsActive = PREDICT_MARKETS.some((l) => pathname.startsWith(l.to));
  const predictActive =
    PREDICT_TOP.some((l) => pathname === l.to || (l.to === "/account/betslips" && pathname.startsWith("/account/betslips")))
    || marketsActive;
  const competeActive = COMPETE_LINKS.some((l) => pathname.startsWith(l.to));
  const performanceActive = PERFORMANCE_LINKS.some((l) => pathname.startsWith(l.to));

  return (
    <header
      className="sticky top-0 z-40 w-full backdrop-blur-md pt-safe"
      data-testid="top-nav"
      style={{
        background: "rgba(9,9,11,0.75)",
        borderBottom: "1px solid var(--v3-line)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo — compact, v3-clean (no subtitle). */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="home-logo-link">
          <img
            src="/ndakubet-logo.jpg"
            alt="NDAKUBET"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-md object-cover shrink-0"
            data-testid="home-logo-img"
          />
          <div className="leading-none min-w-0">
            <div
              className="text-[15px]"
              style={{ fontWeight: 600, letterSpacing: "-0.025em" }}
            >
              Ndakubet
            </div>
          </div>
        </Link>

        {/* Center cluster — 1 pill + 4 dropdowns.  Compact enough to
            leave breathing room on the right for search + utilities
            even on 1440-1920px screens. */}
        <nav className="hidden md:flex items-center gap-1">
          <NavPill
            to={PRIMARY_PILL.to}
            icon={PRIMARY_PILL.icon}
            label={PRIMARY_PILL.label}
            testid={PRIMARY_PILL.testid}
            active={pathname === PRIMARY_PILL.to}
            onClick={() => navigate(PRIMARY_PILL.to)}
          />
          <GroupDropdown
            label="Live" items={LIVE_LINKS} active={liveActive}
            testidPrefix="nav-live" t={t} navigate={navigate} pathname={pathname}
          />
          <PredictDropdown
            active={predictActive} marketsActive={marketsActive}
            t={t} navigate={navigate} pathname={pathname}
          />
          <GroupDropdown
            label="Compete" items={COMPETE_LINKS} active={competeActive}
            testidPrefix="nav-compete" t={t} navigate={navigate} pathname={pathname}
          />
          <GroupDropdown
            label="Performance" items={PERFORMANCE_LINKS} active={performanceActive}
            testidPrefix="nav-performance" t={t} navigate={navigate} pathname={pathname}
          />
          {/* Iter 275g — Gold link, language switcher, theme toggle and
              design-mode toggle all moved to the footer (see
              components/Footer.js).  The top nav keeps a single-line
              category rail so search + primary CTA breathe. */}
        </nav>

        {/* Header search (desktop only, collapsed by default) */}
        <div
          className="hidden lg:block w-32 xl:w-48 focus-within:!w-[320px] xl:focus-within:!w-[420px] transition-[width] duration-300 ease-out shrink-0"
          data-testid="header-search-wrapper"
        >
          <SearchBar key={`hdr-${headerQuery}`} initialQuery={headerQuery} />
        </div>

        {/* Right cluster — utility icons + account menu */}
        <div className="flex items-center gap-2 shrink-0">
          {onPlaceParlayClick && (
            <Button
              onClick={onPlaceParlayClick}
              data-testid="open-parlay-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-9 rounded-full px-4"
            >
              <Zap className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">{t("nav.placeBestParlay")}</span>
              <span className="sm:hidden">Parlay</span>
            </Button>
          )}
          {user ? (
            <AccountMenu user={user} navigate={navigate} logout={logout} t={t} />
          ) : (
            <Link to="/auth">
              <Button
                variant="outline"
                data-testid="nav-login-btn"
                className="h-9 rounded-full px-3.5 border-border bg-card text-foreground hover:bg-muted"
              >
                <LogIn className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{t("common.login")}</span>
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Iter 269 — Mobile nav: mirrors the desktop consolidation.
            One "Live ▾", one "Predict ▾" and one "Compete ▾" — the
            existing bottom nav already covers Live / Schedule /
            History as tabs so the top row stays compact. */}
      <nav
        className="md:hidden flex items-center gap-1.5 px-4 pb-3"
        data-testid="mobile-top-nav"
      >
        <GroupDropdown
          label="Live" items={LIVE_LINKS} active={liveActive}
          testidPrefix="mobile-nav-live" t={t} navigate={navigate} pathname={pathname}
        />
        <PredictDropdown
          active={predictActive} marketsActive={marketsActive}
          t={t} navigate={navigate} pathname={pathname}
        />
        <GroupDropdown
          label="Compete" items={COMPETE_LINKS} active={competeActive}
          testidPrefix="mobile-nav-compete" t={t} navigate={navigate} pathname={pathname}
        />
        <GroupDropdown
          label="Performance"
          items={PERFORMANCE_LINKS.filter((l) => l.to !== "/history")}
          active={performanceActive}
          testidPrefix="mobile-nav-performance"
          t={t} navigate={navigate} pathname={pathname}
        />
      </nav>

      {/* Mobile + tablet search row */}
      <div className="lg:hidden px-4 pb-3">
        <SearchBar key={`mob-${headerQuery}`} initialQuery={headerQuery} />
      </div>
    </header>
  );
};

export default TopNav;
